import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Check, ShieldCheck, Clock, MapPin, Users, ShoppingBag } from "lucide-react";
import { SERVICES, TESTIMONIALS, PROCESS, SITE, HERO_IMAGE, BEFORE_AFTER } from "@/lib/site";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ReviewsSection from "@/components/ReviewsSection";
import Autocomplete from "@/components/Autocomplete";
import { getProductImage, getCategoryContent } from "@/lib/shop-content";
import { searchStatic } from "@/lib/search-index";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Categories we want to surface as landing tiles (in this order).
const FEATURED_CATEGORIES = ["Business Cards", "Postcards", "Flyers", "Banners", "Booklets", "Signs"];
// Prefer showing these products (by id) on the Home landing when they exist.
// Falls back to first-N enabled products if none of these are live.
const HOME_PRODUCT_PREFERENCE = [2, 8, 7, 9, 18, 57];

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});

  useSEO({
    title: null,
    description: "Toronto's trusted studio for custom signs, architectural wraps, window film, wall graphics, and vinyl installation — plus online wholesale print for business cards, flyers, postcards, banners and more. 3M Certified. Serving Toronto, Mississauga, Brampton, Vaughan, Markham, Scarborough. Free quotes & mockups.",
    path: "/",
    image: HERO_IMAGE,
  });

  useEffect(() => {
    let cancelled = false;
    // Featured-first: try the admin-curated home-featured list; fall back to
    // the auto-picked ID preference list if the admin hasn't starred anything yet.
    (async () => {
      let featured = [];
      try {
        const { data } = await axios.get(`${API}/sinalite/products/featured-home?limit=6`);
        featured = data.items || [];
      } catch { /* fall back below */ }

      if (featured.length >= 3 && !cancelled) {
        setFeaturedProducts(featured);
      }
      // Fetch full catalog for category counts + potential fallback grid.
      try {
        const { data } = await axios.get(`${API}/sinalite/products`);
        if (cancelled) return;
        const items = data.items || [];
        if (featured.length < 3) {
          // Fallback: curated IDs + unique-category dedupe.
          const byId = new Map(items.map((p) => [p.id, p]));
          const seenKeys = new Set();
          const pickUnique = (p) => {
            const key = `${p.category}::${getProductImage(p)}`;
            if (seenKeys.has(key)) return false;
            seenKeys.add(key);
            return true;
          };
          const preferred = HOME_PRODUCT_PREFERENCE.map((id) => byId.get(id)).filter(Boolean).filter(pickUnique);
          const filler = items.filter((p) => !preferred.some((pp) => pp.id === p.id)).filter(pickUnique).slice(0, 6 - preferred.length);
          setFeaturedProducts([...preferred, ...filler].slice(0, 6));
        }
        const counts = {};
        for (const p of items) counts[p.category] = (counts[p.category] || 0) + 1;
        setCategoryCounts(counts);
      } catch { /* leave empty — the section will still render CTAs */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Global search: run product search on the backend AND static content search
  // client-side, then interleave them into grouped results for the dropdown.
  const runSiteSearch = useCallback(async (query) => {
    let productResults = [];
    try {
      const { data } = await axios.get(`${API}/search`, { params: { q: query, limit: 6 } });
      productResults = data.results || [];
    } catch { /* silent — static results still show */ }
    const staticResults = searchStatic(query, { limit: 6 });
    // Group nicely: products first, then services/pages/blog/gallery interleaved.
    const groups = [];
    if (productResults.length) groups.push({ label: "Print Shop", items: productResults });
    const svcs = staticResults.filter((s) => s.type === "service");
    if (svcs.length) groups.push({ label: "Services", items: svcs });
    const pages = staticResults.filter((s) => s.type === "page");
    if (pages.length) groups.push({ label: "Pages", items: pages });
    const posts = staticResults.filter((s) => s.type === "blog");
    if (posts.length) groups.push({ label: "Blog", items: posts });
    const gal = staticResults.filter((s) => s.type === "gallery");
    if (gal.length) groups.push({ label: "Gallery", items: gal });
    return { groups };
  }, []);
  return (
    <>
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden grain" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Architectural vinyl wrap installation by Toronto Custom Signs & Wraps — interior commercial space transformation"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/85 to-ink-950/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink-950" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-40 pb-24 w-full">
          <div className="max-w-3xl">
            <div className="reveal reveal-1 eyebrow gold-bar mb-7">Toronto · Mississauga · GTA</div>
            <h1 className="reveal reveal-2 font-display text-5xl sm:text-6xl lg:text-7xl text-white font-medium leading-[1.02] tracking-tight">
              <span className="italic text-gold">Custom signs</span> &amp; wraps, <span className="italic text-purple-hi">now with online print</span>.
            </h1>
            <p className="reveal reveal-3 mt-7 text-white/70 max-w-xl text-lg leading-relaxed">
              Premium signage, architectural wraps, window film, and vinyl installation across the GTA — plus wholesale online print for business cards, flyers, postcards, brochures and banners with live pricing.
            </p>

            {/* Site-wide search — `relative z-30` lifts this above the sibling
                 CTA row which uses `.reveal` (transform creates a stacking
                 context and would otherwise paint over the dropdown). */}
            <div className="reveal reveal-4 mt-8 max-w-xl relative z-30">
              <Autocomplete
                variant="hero"
                testId="hero-search"
                placeholder="Search products, services, gallery, guides…"
                onQuery={runSiteSearch}
                noResultsHint="No matches. Try 'business cards', 'window film', or 'wraps'."
              />
              <div className="mt-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.22em] text-white/45">
                <span>Try:</span>
                {["Business cards", "Window film", "Architectural wraps", "Postcards"].map((k) => (
                  <Link key={k} to={`/shop?category=${encodeURIComponent(k)}`} className="hover:text-gold transition-colors">{k}</Link>
                ))}
              </div>
            </div>

            <div className="reveal reveal-5 mt-8 flex flex-wrap gap-3 relative z-10">
              <Link to="/contact" data-testid="hero-quote-btn" className="btn-gold">Get a Quote <ArrowUpRight size={16} /></Link>
              <Link to="/shop" data-testid="hero-shop-btn" className="btn-ghost"><ShoppingBag size={14} /> Print Shop</Link>
              <a href={SITE.phoneHref} data-testid="hero-call-btn" className="btn-ghost">Call Now</a>
            </div>

            <ul className="reveal reveal-6 mt-12 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-[13px] text-white/70">
              {["3M Certified Preferred Installer","Commercial & Retail Projects","Fast Turnaround Times","Professional Installation","Online Print · Live Pricing","Serving All of the GTA"].map(f => (
                <li key={f} className="flex items-start gap-2"><Check size={14} className="text-gold mt-1 shrink-0"/>{f}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* corner badge */}
        <div className="hidden lg:flex absolute top-32 right-10 flex-col items-end gap-2 text-right">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Est.</div>
          <div className="font-display text-5xl text-gold leading-none">2018</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Toronto · GTA</div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-ink-950 border-y border-white/5 overflow-hidden py-6">
        <div className="flex marquee-track whitespace-nowrap">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-12 px-6 shrink-0">
              {["Architectural Wraps","Channel Letters","Frosted Window Film","Wall Murals","ACM Signs","Vinyl Installation","Reception Signs","Floor Decals","Storefront Graphics"].map((t,i)=>(
                <span key={i} className="flex items-center gap-12 font-display text-2xl text-white/30 hover:text-gold transition-colors">
                  {t} <span className="text-gold">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-28 md:py-36 bg-ink-950" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="eyebrow gold-bar">What we do</div>
              <h2 className="font-display text-4xl md:text-6xl mt-4 tracking-tight max-w-2xl">Our services</h2>
            </div>
            <p className="text-white/60 max-w-md">From storefront signage to full architectural transformations — we handle production and installation end-to-end.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {SERVICES.map((s, i) => {
              const span = i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-4" : i === 3 ? "md:col-span-4" : "md:col-span-4";
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className={`card-dark group relative overflow-hidden ${span} aspect-[4/3] md:aspect-auto md:min-h-[320px]`}>
                    <img src={s.image} alt={`${s.title} — ${s.short} in Toronto and the GTA`} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/70 to-transparent" />
                  <div className="relative h-full p-8 flex flex-col justify-end">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">0{i+1} · {s.short}</div>
                    <h3 className="font-display text-2xl md:text-3xl text-white">{s.title}</h3>
                    <div className="mt-5 flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-white/60 group-hover:text-gold transition-colors">
                      Explore <ArrowUpRight size={14} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRINT SHOP — Featured Products + Categories */}
      <section className="py-28 md:py-36 bg-gradient-to-b from-ink-950 via-ink-900 to-ink-950 relative overflow-hidden" data-testid="print-shop-promo">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-purple/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="eyebrow gold-bar">New · The Print Shop</div>
              <h2 className="font-display text-4xl md:text-5xl mt-4 tracking-tight leading-tight">
                Order <span className="italic text-gold">business cards</span>, flyers &amp; more — online with live pricing.
              </h2>
              <p className="text-white/70 mt-5 leading-relaxed">
                Beyond signs and wraps, we now offer premium wholesale printing online. Configure your job, see live pricing, upload artwork, and we&apos;ll handle production and Canada-wide shipping.
              </p>
            </div>
            <Link to="/shop" data-testid="home-shop-btn" className="btn-gold shrink-0">
              Browse the shop <ArrowUpRight size={16} />
            </Link>
          </div>

          {/* Category tiles */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-14" data-testid="home-category-tiles">
            {FEATURED_CATEGORIES.map((c) => {
              const content = getCategoryContent(c);
              const count = categoryCounts[c];
              return (
                <Link
                  key={c}
                  to={`/shop?category=${encodeURIComponent(c)}`}
                  data-testid={`home-cat-tile-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  className="card-dark overflow-hidden group aspect-square relative hover:border-gold/50 transition-colors"
                >
                  <img
                    src={content.image}
                    alt={c}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-500"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/60 to-transparent" />
                  <div className="relative h-full p-4 flex flex-col justify-end">
                    <div className="font-display text-base md:text-lg text-white group-hover:text-gold transition-colors leading-tight">{c}</div>
                    {count ? (
                      <div className="text-[9px] uppercase tracking-[0.22em] text-white/50 mt-1">{count} product{count !== 1 ? "s" : ""}</div>
                    ) : (
                      <div className="text-[9px] uppercase tracking-[0.22em] text-white/50 mt-1">Explore</div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Featured products grid */}
          {featuredProducts.length > 0 && (
            <div>
              <div className="flex items-baseline justify-between mb-6">
                <div className="text-[11px] uppercase tracking-[0.22em] text-gold">Popular right now</div>
                <Link
                  to="/shop"
                  data-testid="home-featured-all"
                  className="text-[11px] uppercase tracking-[0.18em] text-white/60 hover:text-gold flex items-center gap-1"
                >View all <ArrowUpRight size={12} /></Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-featured-products">
                {featuredProducts.map((p) => (
                  <Link
                    key={p.id}
                    to={`/shop/product/${p.id}`}
                    data-testid={`home-featured-product-${p.id}`}
                    className="card-dark overflow-hidden hover:border-gold/50 transition-colors group flex flex-col"
                  >
                    <div className="aspect-[4/3] bg-ink-800 relative overflow-hidden">
                      <img
                        src={getProductImage(p)}
                        alt={p.seo_alt_text || p.name}
                        loading="lazy"
                        className="w-full h-full object-contain p-8 group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink-950/50 to-transparent pointer-events-none" />
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-gold mb-2">{p.category}</div>
                      <div className="font-display text-base text-white group-hover:text-gold transition-colors leading-snug line-clamp-2">{(p.name || "").trim()}</div>
                      <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between">
                        <span className="text-[11px] uppercase tracking-[0.22em] text-white/55">Configure &amp; price</span>
                        <ShoppingBag size={13} className="text-gold" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Benefits row */}
          <ul className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm text-white/80 max-w-4xl mx-auto">
            {["Live pricing","Free artwork check","Fast turnaround","Canada-wide shipping"].map((f) => (
              <li key={f} className="flex items-center gap-2 justify-center md:justify-start"><Check size={14} className="text-gold shrink-0" /> {f}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="py-28 md:py-36 bg-ink-900 border-y border-white/5" data-testid="before-after-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <div className="eyebrow">Portfolio</div>
            <h2 className="font-display text-4xl md:text-6xl mt-3 tracking-tight">Real transformations</h2>
            <p className="text-white/60 max-w-xl mx-auto mt-4">Drag the gold handle to reveal the before-and-after.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <BeforeAfterSlider
              before={BEFORE_AFTER[0].before}
              after={BEFORE_AFTER[0].after}
              beforeLabel="Before"
              afterLabel="After"
            />
            <BeforeAfterSlider
              before={BEFORE_AFTER[1].before}
              after={BEFORE_AFTER[1].after}
              beforeLabel="Before"
              afterLabel="After"
            />
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-28 md:py-36 bg-ink-950" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow gold-bar">Why us</div>
            <h2 className="font-display text-4xl md:text-5xl mt-4 tracking-tight">Built for businesses that care how they look.</h2>
            <p className="text-white/60 mt-6 leading-relaxed">We&apos;re a premium signage and architectural wrap studio serving brands across Toronto and the GTA. Every project is treated as a permanent extension of your brand.</p>
            <Link to="/about" className="btn-ghost mt-8 inline-flex" data-testid="why-learn-more">Learn more <ArrowUpRight size={14}/></Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              { icon: ShieldCheck, t: "Professional Quality", d: "Premium 3M and Avery materials, executed by a certified install team." },
              { icon: Users, t: "Experienced Installers", d: "Commercial wraps, retail branding, and custom vinyl applications." },
              { icon: Clock, t: "Fast Turnaround", d: "Quick production and installation timelines without compromising finish." },
              { icon: MapPin, t: "GTA Service Area", d: "Toronto, Mississauga, Brampton, Vaughan, Markham, Scarborough." },
            ].map((b,i) => (
              <div key={i} className="card-dark p-7" data-testid={`why-card-${i}`}>
                <b.icon size={22} className="text-gold mb-5" />
                <h4 className="font-display text-xl">{b.t}</h4>
                <p className="text-white/55 text-sm mt-3 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-28 md:py-36 bg-ink-900 border-y border-white/5 relative overflow-hidden" data-testid="process-section">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-purple/20 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="eyebrow">How we work</div>
          <h2 className="font-display text-4xl md:text-6xl mt-3 tracking-tight max-w-3xl">A four-step process designed to be effortless.</h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-px bg-white/5">
            {PROCESS.map((p) => (
              <div key={p.n} className="bg-ink-900 p-8 hover:bg-ink-800 transition-colors" data-testid={`process-${p.n}`}>
                <div className="font-display text-5xl text-gold/70">{p.n}</div>
                <h4 className="font-display text-xl mt-6">{p.title}</h4>
                <p className="text-white/55 text-sm mt-3 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <ReviewsSection />

      {/* CTA */}
      <section className="py-28 md:py-32 bg-gradient-to-b from-ink-950 to-ink-900 relative overflow-hidden" data-testid="home-cta-section">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-purple/20 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <div className="eyebrow">Ready to transform your business?</div>
          <h2 className="font-display text-4xl md:text-6xl mt-4 tracking-tight">Get a free quote &amp; mockup for your project.</h2>
          <p className="text-white/60 max-w-2xl mx-auto mt-6">Custom signs, architectural wraps, vinyl graphics, and installation — designed and delivered in the GTA.</p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" data-testid="cta-quote-btn" className="btn-gold">Request a Free Quote <ArrowUpRight size={16}/></Link>
            <a href={SITE.phoneHref} data-testid="cta-call-btn" className="btn-ghost">{SITE.phone}</a>
          </div>
        </div>
      </section>
    </>
  );
}
